// src/components/study/CardActionsMenu.tsx
// =====================================================================
// ✅ MUDANÇAS:
// 1. Botão "Adicionar aos meus decks" trocou de lugar com "Reportar erro"
//    → Add agora é menu-item-4 (antes era item-3)
//    → Report agora é menu-item-3 (antes era item-4)
// 2. Botão de adicionar só aparece para admin e vendor
// 3. Ícone trocado de Plus para FolderPlus
// =====================================================================

import React, { useState, useEffect } from 'react';
import { BookOpen, AlertTriangle, Bookmark, FileText } from 'lucide-react';
import SavedToast from './SavedToast';
import OrganizeModal from './OrganizeModal';
import './CardActionsMenu.css';

interface CardActionsMenuProps {
    hasReferences: boolean;
    onShowReferences: () => void;
    onReportError: () => void;
    onReloadSavedCards: () => void;
    onToggleSupportContent: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    deckId: string;
    subdeckId: string;
    hasSupportContent: boolean;
    cardId: string;
    cardContent: {
        front: string;
        back: string;
        supportContent?: string;
        references?: string;
    };
    hierarchy: {
        cycleName: string | null;
        cycleId: string | null;
        deckName: string;
        subdeckName: string;
    };
    fromRyan?: boolean;
    ryanSpaceName?: string;
    ryanSpaceId?: string;
    hideActionsMenu?: boolean;
}

const CardActionsMenu: React.FC<CardActionsMenuProps> = ({
    hasReferences,
    onShowReferences,
    onReportError,
    onReloadSavedCards,
    onToggleSupportContent,
    hasSupportContent,
    onShowToast,
    deckId,
    subdeckId,
    cardId,
    cardContent,
    hierarchy,
    fromRyan = false,
    ryanSpaceName,
    ryanSpaceId,
    hideActionsMenu = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isLoadingSave, setIsLoadingSave] = useState(false);

    const [showSavedToast, setShowSavedToast] = useState(false);
    const [lastSavedCardId, setLastSavedCardId] = useState<string | null>(null);

    const [showOrganizeModal, setShowOrganizeModal] = useState(false);
    const [organizeTargetId, setOrganizeTargetId] = useState<string | null>(null);

    // ✅ Resetar estado ao trocar de card
    useEffect(() => {
        if (!cardId) return;

        setIsSaved(false);
        setIsOpen(false);
        setShowSavedToast(false);
        setLastSavedCardId(null);

        if (deckId && subdeckId) {
            const timer = setTimeout(() => {
                checkIfSaved();
            }, 400);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    const checkIfSaved = async () => {
        if (!deckId || !subdeckId || !cardId) return;
        try {
            const token = localStorage.getItem('accessToken');
            const checkUrl = fromRyan
                ? `/api/ryan/saved/check/${deckId}/${subdeckId}/${cardId}`
                : `/api/saved-cards/check/${deckId}/${subdeckId}/${cardId}`;
            const response = await fetch(checkUrl, { headers: { Authorization: `Bearer ${token}` } });
            if (response.ok) {
                const data = await response.json();
                setIsSaved(data.isSaved);
            }
        } catch (error) {
            console.error('Erro ao verificar card salvo:', error);
        }
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(prev => !prev);
    };

    const handleAction = (e: React.MouseEvent, action: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        action();
        setIsOpen(false);
    };

    const handleToggleSave = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isLoadingSave || !deckId || !subdeckId || !cardId) return;

        setIsLoadingSave(true);
        const previousState = isSaved;
        setIsSaved(!isSaved);

        try {
            const token = localStorage.getItem('accessToken');

            const payload = fromRyan ? {
                deckId: deckId || '',
                subdeckId: subdeckId || '',
                cardId,
                hierarchy: {
                    spaceName: ryanSpaceName || '',
                    spaceId: ryanSpaceId || '',
                    subdeckName: hierarchy?.subdeckName || '',
                },
            } : {
                deckId: deckId || 'saved',
                subdeckId: subdeckId || 'saved',
                cardId,
                hierarchy: {
                    cycleName: hierarchy?.cycleName || null,
                    cycleId: hierarchy?.cycleId || null,
                    deckName: hierarchy?.deckName || 'Deck Salvo',
                    subdeckName: hierarchy?.subdeckName || 'Pessoal'
                },
                cardContent: {
                    front: cardContent?.front || '',
                    back: cardContent?.back || '',
                    supportContent: cardContent?.supportContent || '',
                    references: cardContent?.references || ''
                }
            };

            const toggleUrl = fromRyan ? '/api/ryan/saved/toggle' : '/api/saved-cards/toggle';
            const response = await fetch(toggleUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                setIsSaved(previousState);
                throw new Error('Falha ao salvar');
            }

            const data = await response.json();
            setIsSaved(data.isSaved);

            if (data.isSaved && data.savedCardId) {
                setLastSavedCardId(data.savedCardId);
                setShowSavedToast(true);
            }

            onReloadSavedCards?.();

        } catch (error) {
            console.error('Erro ao salvar card:', error);
            setIsSaved(previousState);
            onShowToast('Erro ao salvar flashcard', 'error');
        } finally {
            setIsLoadingSave(false);
        }
    };

    const handleOrganizeFromToast = (savedCardId: string) => {
        setOrganizeTargetId(savedCardId);
        setShowOrganizeModal(true);
    };

    return (
        <>
            {/* SVG Filter Gooey */}
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" className="gooey-svg">
                <defs>
                    <filter id="gooey-effect">
                        <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="10" />
                        <feColorMatrix
                            in="blur"
                            mode="matrix"
                            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
                            result="goo"
                        />
                        <feComposite in2="goo" in="SourceGraphic" result="mix" />
                    </filter>
                </defs>
            </svg>

            <nav
                className="card-actions-menu-gooey"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {!hideActionsMenu && (
                    <>
                        <input
                            type="checkbox"
                            className="menu-open-checkbox"
                            id={`menu-open-${cardId}`}
                            checked={isOpen}
                            onChange={() => setIsOpen(!isOpen)}
                        />

                        <label
                            className="menu-open-button"
                            htmlFor={`menu-open-${cardId}`}
                            onClick={handleToggle}
                            title="Menu"
                        >
                            <span className="hamburger hamburger-1" />
                            <span className="hamburger hamburger-2" />
                            <span className="hamburger hamburger-3" />
                        </label>

                        <div className="gooey-wrapper">
                            <button
                                className={`menu-item menu-item-1 ${!hasReferences ? 'disabled' : ''}`}
                                onClick={(e) => handleAction(e, () => hasReferences && onShowReferences())}
                                disabled={!hasReferences}
                                type="button"
                                title="Referências"
                            >
                                <BookOpen size={18} strokeWidth={2.5} />
                            </button>

                            <button
                                className={`menu-item menu-item-2 saved-item ${isSaved ? 'saved' : ''}`}
                                onClick={handleToggleSave}
                                disabled={isLoadingSave}
                                type="button"
                                title={isSaved ? 'Remover dos salvos' : 'Salvar card'}
                            >
                                <Bookmark
                                    size={18}
                                    strokeWidth={2.5}
                                    fill={isSaved ? 'currentColor' : 'none'}
                                />
                            </button>

                            <button
                                className="menu-item menu-item-3"
                                onClick={(e) => handleAction(e, onReportError)}
                                type="button"
                                title="Reportar erro"
                            >
                                <AlertTriangle size={18} strokeWidth={2.5} />
                            </button>
                        </div>
                    </>
                )}

                {/* Botão de Mais Detalhes — sempre visível */}
                <button
                    className={`details-btn ${!hasSupportContent ? 'disabled' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (hasSupportContent) onToggleSupportContent();
                    }}
                    disabled={!hasSupportContent}
                    type="button"
                    aria-label="Mais detalhes"
                    data-tooltip="Conteúdo de apoio"
                >
                    <FileText size={16} strokeWidth={2.5} />
                </button>
            </nav>

            {/* ✅ FORA do <nav> — fixed funciona corretamente */}
            <SavedToast
                isVisible={showSavedToast}
                savedCardId={lastSavedCardId}
                onOrganize={handleOrganizeFromToast}
                onClose={() => setShowSavedToast(false)}
            />

            <OrganizeModal
                isOpen={showOrganizeModal}
                savedCardId={organizeTargetId}
                onClose={() => { setShowOrganizeModal(false); setOrganizeTargetId(null); }}
                onShowToast={onShowToast}
                fromRyan={fromRyan}
            />
        </>
    );
};

export default CardActionsMenu;